import { Operation } from '@/types/general';
import { ManageForm } from '@components/forms/ManageForm';
import { useResellerMutation, useNameMapQuery, useResellerQuery } from '@features/redux/apis/org';
import { useSelector } from 'react-redux';
import { Box, Button, Checkbox, FormControl, FormControlLabel, Grid, MenuItem, Select, TextField } from '@mui/material';
import { getIdDisplayValue } from '@utils/index';
import { GridSearchSelect, type SearchSelectOption } from '@components/forms/GridSearchSelect';
import type { RootState } from '@features/redux/store';
import { FormRequiredText } from '@components/forms/text/base/FormRequiredText';
import { Validate } from '@utils/validate';
import { ResellerEffects } from './ResellerEffects';
import { Query } from '@features/redux/apis/utils';
import { BILLING_OPTIONS, DEFAULT_BILLING_OPTIONS, WARRANTY_OPTIONS } from '@consts/company';
import { FormDisabledText } from '@components/forms/text/base/FormDisabledText';
import { FormSelect } from '@components/forms/dropdowns/base/FormSelect';

import type { ManageProps } from '../Panel';
import { useEffect, useState } from 'react';

// ? Don't use withDraft unless we need to. We don't need to
// ? create a draft in the ResellerEffects component, for example.
const useResellers = Query.withDraft(useResellerQuery, {
    ResellerCompanyID: 0,
    CompanyName: '',
    Inactive: false,
    QBName: undefined,
    SalesRepTrackingEnabled: null,
    ...DEFAULT_BILLING_OPTIONS,
});

export const Reseller = ({ id: resellerId, operation, onSuccess }: ManageProps) => {
    // const navigate = useNavigate();

    const { role } = useSelector(({ auth }: RootState) => auth.Roles);

    const { data: nameMap } = useNameMapQuery({});

    const {
        draft,
        data,
        isFetching: queryLoading,
    } = useResellers({ resellerId: +resellerId! }, { skip: operation === Operation.Create });

    const [mutate, { isLoading: mutationLoading }] = useResellerMutation({
        // We don't want to use the mutation cache if we're creating a new reseller
        fixedCacheKey: operation === Operation.Edit ? 'ResellerMutation' : 'ResellerCreation',
    });

    const isLoading = queryLoading || mutationLoading;

    const [draftState, setDraftState] = draft;

    /**
     * Following pattern from Location page for Consent to sell (cts) 
    */
    const [salesRepTracking, setSalesRepTracking] = useState<boolean | 'inherit'>('inherit');
    useEffect(() => void setSalesRepTracking(data?.SalesRepTrackingEnabled === null ? 'inherit': Boolean(data?.SalesRepTrackingEnabled)), [data?.SalesRepTrackingEnabled]);
    
    const canEditPrices = role.prices.edit && role.companies.fullAccess;

    return (
        <>
            <ResellerEffects />
            <ManageForm
                title={operation === Operation.Create ? 'Create New Reseller' : 'Manage Reseller'}
                loading={isLoading}
                size={12}>
                <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                    <Grid container spacing={2} marginTop='10px'>
                        {operation === Operation.Edit && role.isAdmin && (
                            <>
                                <FormDisabledText
                                    size={12}
                                    label='Reseller ID'
                                    value={data?.CompanyID?.toString() || 'N/A'}
                                />
                                <FormDisabledText
                                    size={12}
                                    label='Linked Company ID'
                                    value={getIdDisplayValue(data?.ResellerCompanyID)}
                                />
                            </>
                        )}

                        <GridSearchSelect
                            state={draftState}
                            dispatch={setDraftState}
                            getLabel={(idKey) => nameMap?.companies[idKey] ?? ''}
                            idKey={'ResellerCompanyID'}
                            size={12}
                            label='Linked Company'
                            options={
                                Object.entries(nameMap?.companies ?? {}).map(([key, value]) => ({
                                    id: key,
                                    name: value,
                                })) as SearchSelectOption[]
                            }
                        />

                        <FormRequiredText
                            size={12}
                            stateKey='CompanyName'
                            label='Reseller Name'
                            draft={draft}
                            validateFn={Validate.name}
                            helperText={{
                                valid: '',
                                invalid: 'Please provide a valid reseller name',
                            }}
                        />

                        {role.isAdmin && role.prices.edit && (
                            <FormRequiredText
                                disabled={!role.companies.fullAccess}
                                size={12}
                                stateKey='QBName'
                                label='QuickBooks Name'
                                draft={draft}
                                validateFn={Validate.name}
                                helperText={{
                                    valid: '',
                                    invalid: 'Please provide a valid reseller name',
                                }}
                            />
                        )}

                        <FormSelect
                            disable={!canEditPrices}
                            size={12}
                            label='Billing'
                            draft={draft}
                            stateKey='CutPlanID'
                            options={BILLING_OPTIONS}
                        />
                        <FormSelect
                            disable={!canEditPrices}
                            size={12}
                            label='Warranty'
                            draft={draft}
                            stateKey='WarrantyPlanID'
                            options={WARRANTY_OPTIONS}
                        />

                        {role.stores.edit && (
                            <Box sx={{margin: '15px'}}>

                                   <TextField
                                        sx={{ userSelect: 'none', width: '300px' }}
                                        value={salesRepTracking}
                                        select
                                        onChange={({ target }) => {
                                            setSalesRepTracking(target.value as boolean | 'inherit');
                                            setDraftState((prev) => ({ ...prev, SalesRepTrackingEnabled: target.value === 'inherit' ? null: target.value as unknown as boolean | null }));
                                        }}
                                        label={'Sales Rep Tracking'}
                                    >
                                        <MenuItem value={'inherit'}>
                                            Inherit from Company
                                        </MenuItem>

                                        <MenuItem value={true as any}>
                                            Enabled
                                        </MenuItem>

                                        <MenuItem value={false as any}>
                                            Disabled
                                        </MenuItem>
                                    </TextField>
                                    
                                    

                            </Box>
                        )}

                        <Grid item xs={12} sm={12} md={12} display='flex' gap='10px'>
                            <Button
                                type='submit'
                                disabled={!Validate.Org.reseller(draftState)}
                                size={'large'}
                                variant={'contained'}
                                color={'orange'}
                                onClick={async () => {
                                    await mutate({
                                        method: operation === Operation.Create ? 'POST' : 'PATCH',
                                        reseller: draftState,
                                    });

                                    onSuccess?.();
                                }}>
                                {operation === Operation.Create ? 'Create' : 'Update'}
                            </Button>
                        </Grid>
                    </Grid>
                </Grid>
            </ManageForm>
        </>
    );
};
